import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, Trash2, Loader2, ShieldAlert, UserCog, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// [FIX] Smart Bridge URL Logic (Copies what works in AuthContext)
const getAPIUrl = () => {
  const host = window.location.hostname;
  if (host.includes("emergentagent.com")) {
    return `https://${host.replace("-3000", "-8000")}/api`;
  }
  return `${process.env.REACT_APP_BACKEND_URL}/api`;
};

const API = getAPIUrl();

const ROLE_COLORS = {
  admin: "bg-emerald-100 text-emerald-800 border-emerald-200",
  operator: "bg-blue-100 text-blue-800 border-blue-200",
};

export default function AdminUsers() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  useEffect(() => {
    // Only fetch if we are actually an admin
    if (isAdmin) fetchAdmins();
    else setLoading(false);
  }, [isAdmin]);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      // Axios will use the Global Interceptor from AuthContext for the token
      const res = await axios.get(`${API}/admin-users`);
      setAdmins(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error("Failed to fetch system administrators");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (username) => {
    if (username === 'admin') return toast.error("Root admin cannot be deleted");
    
    // Simple confirm for safety
    if (!window.confirm(`Are you sure you want to delete ${username}?`)) return; 
    
    try {
      await axios.delete(`${API}/admin-users/${username}`);
      toast.success("Administrator removed");
      fetchAdmins(); // Refresh list
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  // BLOCK NON-ADMINS
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6 border border-red-100">
          <ShieldAlert className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Access Restricted</h2>
        <p className="text-slate-500 text-center max-w-sm mt-2">
          You do not have the required permissions. Contact root admin.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-slate-800">System Administrators</h2>
          <p className="text-slate-500 mt-1">Manage dashboard access and role permissions</p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)} className="bg-[#1a3d2e] hover:bg-[#2e5c44] text-white shadow-md">
          <UserPlus className="mr-2 h-4 w-4" /> Add Administrator
        </Button>
      </div>

      {/* TABLE */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100">
          <div className="flex items-center gap-2 text-emerald-700">
            <UserCog className="w-5 h-5" />
            <span className="font-bold text-sm uppercase tracking-wider">Access Control List</span>
          </div>
        </CardHeader>
        <div className="p-0">
          <Table>
            <TableHeader className="bg-slate-50/30 font-bold">
              <TableRow>
                <TableHead className="pl-6">Identity</TableHead>
                <TableHead>System Role</TableHead>
                <TableHead className="text-right pr-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-10">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-600" />
                  </TableCell>
                </TableRow>
              ) : admins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-10 text-slate-400 font-medium">No administrators found.</TableCell>
                </TableRow>
              ) : admins.map((admin) => (
                <TableRow key={admin.id} className="group hover:bg-slate-50/50 transition-colors">
                  <TableCell className="pl-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center font-bold text-emerald-700 border border-emerald-200 shadow-sm">
                         {admin.username.substring(0,2).toUpperCase()}
                      </div>
                      <span className="font-semibold text-slate-700">{admin.username}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${ROLE_COLORS[admin.role] || ROLE_COLORS.operator} border shadow-none capitalize px-3 py-1`}>
                      {admin.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right pr-6">
                    {admin.username !== 'admin' && (
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(admin.username)} className="text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 className="w-4.5 h-4.5" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* CREATE MODAL */}
      <AdminUserDialog 
        open={isCreateOpen} 
        onOpenChange={setIsCreateOpen} 
        onSuccess={() => { setIsCreateOpen(false); fetchAdmins(); }}
      />
    </div>
  );
}

// --- SUB-COMPONENT: CREATE FORM ---
function AdminUserDialog({ open, onOpenChange, onSuccess }) {
  const [formData, setFormData] = useState({ username: "", password: "", role: "operator" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setFormData({ username: "", password: "", role: "operator" });
  }, [open]);

  const handleSubmit = async () => {
    if (!formData.username || !formData.password) return toast.error("Username and password required");
    
    setLoading(true);
    try {
      // Uses the same API bridge
      await axios.post(`${API}/admin-users`, formData);
      toast.success("Administrator Created");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Creation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-[#1a3d2e] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5"/> Grant System Access
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Username</Label>
            <Input 
              value={formData.username} 
              onChange={e => setFormData({...formData, username: e.target.value})} 
              placeholder="e.g. jdoe_admin"
            />
          </div>
          <div className="space-y-2">
            <Label>Password</Label>
            <Input 
              type="password" 
              value={formData.password} 
              onChange={e => setFormData({...formData, password: e.target.value})} 
              placeholder="Secure password"
            />
          </div>
          <div className="space-y-2">
            <Label>Role Permission</Label>
            <Select 
              value={formData.role} 
              onValueChange={v => setFormData({...formData, role: v})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="operator">Operator (View Only)</SelectItem>
                <SelectItem value="admin">Administrator (Full Access)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-[#1a3d2e] hover:bg-[#254f3b] text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null} 
            Create Access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}